// First-touch (persistent) + session (session-scoped) marketing attribution.
//
// Storage: first-party browser storage only, no fingerprinting.
//   localStorage   'tfg_first_touch'  — written once, never overwritten
//   localStorage   'tfg_visits'       — visit counter / last-visit timestamp (repeat-visitor signals)
//   sessionStorage 'tfg_session_attr' — current session acquisition source
//
// SPA navigation never resets attribution: session values are computed once per
// browser session (or when a genuinely new attributable entry happens, i.e. UTMs
// or an external referrer arrive) and internal referrers are ignored.

const FIRST_TOUCH_KEY = 'tfg_first_touch';
const SESSION_KEY = 'tfg_session_attr';
const VISITS_KEY = 'tfg_visits';

// Landing context captured at boot (memory only) so attribution stays correct
// even when consent is granted after SPA navigation.
const BOOT = typeof window === 'undefined'
  ? { search: '', path: '/', referrer: '' }
  : { search: window.location.search, path: window.location.pathname, referrer: document.referrer || '' };

export interface RawAttribution {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  landing_page?: string;
  referrer?: string;
  referring_domain?: string;
}

export interface FirstTouchProperties {
  first_touch_source?: string;
  first_touch_medium?: string;
  first_touch_campaign?: string;
  first_touch_content?: string;
  first_touch_term?: string;
  first_touch_landing_page?: string;
  first_touch_referrer?: string;
  first_touch_referring_domain?: string;
  first_touch_timestamp?: string;
}

export interface SessionProperties {
  session_source?: string;
  session_medium?: string;
  session_campaign?: string;
  session_content?: string;
  session_term?: string;
  session_landing_page?: string;
  session_referrer?: string;
  session_referring_domain?: string;
}

export interface VisitProperties {
  visit_number?: number;
  is_repeat_visitor?: boolean;
  days_since_last_visit?: number;
}

function safeGet(store: Storage | undefined, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(store: Storage | undefined, key: string, value: string) {
  try {
    store?.setItem(key, value);
  } catch {
    /* storage unavailable (private mode / blocked) — analytics degrades gracefully */
  }
}

function hostOf(url: string): string | undefined {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}

function isInternalReferrer(referrer: string): boolean {
  const host = hostOf(referrer);
  if (!host) return true; // no referrer → treat as direct, not a referral
  const self = window.location.hostname.replace(/^www\./, '');
  return host === self || host.endsWith(`.${self}`) || self.endsWith(`.${host}`);
}

/** Read UTMs / referrer for the current page load. */
function readCurrentAttribution(): { attr: RawAttribution; attributable: boolean } {
  const params = new URLSearchParams(BOOT.search);
  const utmSource = params.get('utm_source') || undefined;
  const utmMedium = params.get('utm_medium') || undefined;
  const utmCampaign = params.get('utm_campaign') || undefined;
  const utmContent = params.get('utm_content') || undefined;
  const utmTerm = params.get('utm_term') || undefined;
  // Common paid-click identifiers still count as an attributable entry
  const clickId = params.get('gclid') || params.get('fbclid') || params.get('li_fat_id') || undefined;

  const referrer = BOOT.referrer;
  const externalReferrer = referrer && !isInternalReferrer(referrer) ? referrer : '';
  const referringDomain = externalReferrer ? hostOf(externalReferrer) : undefined;

  const hasUtm = Boolean(utmSource || utmMedium || utmCampaign || clickId);

  let source = utmSource;
  let medium = utmMedium;
  if (!source) {
    if (clickId) {
      source = params.get('gclid') ? 'google' : params.get('fbclid') ? 'facebook' : 'linkedin';
      medium = medium || 'cpc';
    } else if (referringDomain) {
      source = referringDomain;
      medium = medium || 'referral';
    } else {
      source = 'direct';
      medium = medium || 'none';
    }
  }

  return {
    attr: {
      source,
      medium,
      campaign: utmCampaign,
      content: utmContent,
      term: utmTerm,
      landing_page: BOOT.path,
      referrer: externalReferrer || undefined,
      referring_domain: referringDomain,
    },
    attributable: hasUtm || Boolean(externalReferrer),
  };
}

function toFirstTouch(attr: RawAttribution): FirstTouchProperties {
  return {
    first_touch_source: attr.source,
    first_touch_medium: attr.medium,
    first_touch_campaign: attr.campaign,
    first_touch_content: attr.content,
    first_touch_term: attr.term,
    first_touch_landing_page: attr.landing_page,
    first_touch_referrer: attr.referrer,
    first_touch_referring_domain: attr.referring_domain,
    first_touch_timestamp: new Date().toISOString(),
  };
}

function toSession(attr: RawAttribution): SessionProperties {
  return {
    session_source: attr.source,
    session_medium: attr.medium,
    session_campaign: attr.campaign,
    session_content: attr.content,
    session_term: attr.term,
    session_landing_page: attr.landing_page,
    session_referrer: attr.referrer,
    session_referring_domain: attr.referring_domain,
  };
}

let firstTouchCache: FirstTouchProperties | null = null;
let sessionCache: SessionProperties | null = null;
let visitCache: VisitProperties | null = null;
let initialized = false;

/** Resolve (and persist on first visit) all attribution state. Safe to call repeatedly. */
export function initAttribution(): {
  firstTouch: FirstTouchProperties;
  session: SessionProperties;
  visit: VisitProperties;
  isNewFirstTouch: boolean;
} {
  const { attr, attributable } = readCurrentAttribution();

  // ---- First touch: written once, never overwritten ----
  let isNewFirstTouch = false;
  const storedFirstTouch = safeGet(localStorage, FIRST_TOUCH_KEY);
  if (storedFirstTouch) {
    try {
      firstTouchCache = JSON.parse(storedFirstTouch) as FirstTouchProperties;
    } catch {
      firstTouchCache = null;
    }
  }
  if (!firstTouchCache) {
    firstTouchCache = toFirstTouch(attr);
    safeSet(localStorage, FIRST_TOUCH_KEY, JSON.stringify(firstTouchCache));
    isNewFirstTouch = true;
  }

  // ---- Session: per browser session, refreshed on a new attributable entry ----
  const storedSession = safeGet(sessionStorage, SESSION_KEY);
  if (storedSession) {
    try {
      sessionCache = JSON.parse(storedSession) as SessionProperties;
    } catch {
      sessionCache = null;
    }
  }
  const sessionIsDirect = !sessionCache || sessionCache.session_source === 'direct';
  if (!sessionCache || (attributable && sessionIsDirect && !initialized)) {
    sessionCache = toSession(attr);
    safeSet(sessionStorage, SESSION_KEY, JSON.stringify(sessionCache));
  }

  // ---- Visit counter (repeat-visitor intent signals) ----
  if (!visitCache) {
    let visits = 0;
    let lastVisit: number | undefined;
    const storedVisits = safeGet(localStorage, VISITS_KEY);
    if (storedVisits) {
      try {
        const parsed = JSON.parse(storedVisits) as { count?: number; last?: number };
        visits = parsed.count || 0;
        lastVisit = parsed.last;
      } catch {
        /* ignore malformed */
      }
    }
    const isNewSession = !storedSession;
    const nextCount = isNewSession ? visits + 1 : Math.max(visits, 1);
    if (isNewSession) {
      safeSet(localStorage, VISITS_KEY, JSON.stringify({ count: nextCount, last: Date.now() }));
    }
    visitCache = {
      visit_number: nextCount,
      is_repeat_visitor: nextCount > 1,
      days_since_last_visit:
        lastVisit && isNewSession
          ? Math.floor((Date.now() - lastVisit) / 86_400_000)
          : undefined,
    };
  }

  initialized = true;
  return { firstTouch: firstTouchCache, session: sessionCache, visit: visitCache, isNewFirstTouch };
}

export function getFirstTouch(): FirstTouchProperties {
  return firstTouchCache ?? initAttribution().firstTouch;
}

/** Session-scoped attribution — attached to events, never stored as a permanent person property. */
export function getSessionAttribution(): SessionProperties {
  return sessionCache ?? initAttribution().session;
}

export function getVisitProperties(): VisitProperties {
  return visitCache ?? initAttribution().visit;
}

/** Remove attribution/visit storage when all measurement consent is withdrawn. */
export function clearAttributionStorage() {
  try {
    localStorage.removeItem(FIRST_TOUCH_KEY);
    localStorage.removeItem(VISITS_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
  firstTouchCache = null;
  sessionCache = null;
  visitCache = null;
  initialized = false;
}
